import PriceChangeIcon from '@mui/icons-material/PriceChange';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '../../../../../../../shared/components/table/table.component';
import {
  Column,
  Footer,
} from '../../../../../../../shared/components/table/components/common.components';
import { Theme as theme } from '../../../../../../../shared/styles/theme/theme.ts';
import { localeFormatterHelper } from '../../../../../../../shared/helpers/formatter/localeFormatter.helper.ts';
import { configurationService } from '../../../../services/configuration.service';
import { ExecuteArticleSpecialPricesImportMutationRes } from '../../../../services/configuration.service';
import { responseHandler } from '../../../../../../../shared/responseHandler/responseHandler.ts';
import { configurationTabLoadingService } from '../../../../configurationTabLoading.service.ts';
import { snackbarService } from '../../../../../../../shared/components/snackbar/service/snackbar.service.ts';
import { C_Special_Price_Import_Mode } from '../../../../../../../graphql/generatedModel.ts';
import { ISpecialPriceImportData } from '../import.tab.tsx';
import { IOptionsData } from './importDetailsInfo.component.tsx';
import { specialPricesViewRows } from './views/viewRows';
import { IImportAlert, TableHeader } from './tableHeader.component';
import { importGridHeight, visibleRows } from '../helpers/importGridLayout';

const PRICE_CATEGORIES_COUNT = 10;

export const SpecialPricesTable: FC<ISpecialPricesTable> = ({
  dataFromFile,
  collapsedRows,
  existingNumbers,
  options,
}) => {
  const { t } = useTranslation();
  const [gridFullData, setGridFullData] = useState<ISpecialPriceImportData[]>([]);
  const [isAllDataShown, setIsAllDataShown] = useState(false);
  const [selectAll, setSelectAll] = useState<boolean>(true);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [mode, setMode] = useState<C_Special_Price_Import_Mode>(
    C_Special_Price_Import_Mode.SPIM1_MERGE_PERIODS,
  );
  const [isReplaceConfirmOpen, setIsReplaceConfirmOpen] = useState(false);
  const icon = <PriceChangeIcon color='primary' fontSize='small' />;

  useEffect(() => {
    const allData = dataFromFile.map((item) => ({
      ...item,
      isDuplicate: dataFromFile.some((value) => value.id !== item.id && isSamePeriod(value, item)),
      isUnknownArticle: !existingNumbers.some((value) => value === item.articleNo),
      isOverlapping: dataFromFile.some(
        (value) => value.id !== item.id && !isSamePeriod(value, item) && isOverlapping(value, item),
      ),
    }));

    allData.sort(
      (a, b) =>
        a.articleNo.localeCompare(b.articleNo) ||
        String(a.fromDate).localeCompare(String(b.fromDate)),
    );
    // Rows for an article the tenant does not have would be skipped by the
    // import anyway, so they start unselected instead of quietly inflating the
    // "skipped" count in the result message.
    const importable = allData.filter((item) => !item.isUnknownArticle);
    setSelectedIds(new Set(importable.map((v) => v.id)));
    setSelectAll(importable.length > 0);
    setGridFullData(allData);
    setIsAllDataShown(allData.length <= collapsedRows);
  }, [dataFromFile, existingNumbers, collapsedRows]);

  useEffect(() => {
    const executeImportSub = configurationService.sub
      .executeArticleSpecialPricesImport()
      .pipe(
        // `null`, not a zeroed result: responseHandler already shows the error
        // snackbar and then emits this value downstream, so a result-shaped
        // fallback would make the subscriber report a successful import of
        // nothing right underneath that error. It also keeps the counts out of
        // this closure, which the empty dependency array would otherwise pin to
        // the first (empty) render of gridFullData.
        responseHandler<ExecuteArticleSpecialPricesImportMutationRes | null>({
          errorReturnType: null,
        }),
      )
      .subscribe((data) => {
        configurationTabLoadingService.pub.loading(false);

        // Failure: keep the parsed rows on screen so the import can be retried
        // without uploading the file again.
        if (!data) {
          return;
        }

        setGridFullData([]);
        snackbarService.pub.show({
          type: 'success',
          content: t('settings.special_prices_import_successful', {
            recordsInserted: data.recordsInserted,
            recordsUpdated: data.recordsUpdated,
            recordsSkipped: data.recordsSkipped,
          }),
        });
      });

    return () => {
      executeImportSub.unsubscribe();
    };
  }, []);

  // A period for an article the tenant does not have can never be imported, so it
  // is not selectable either - otherwise the header checkbox, the row checkboxes
  // and the footer count (which excludes those rows) tell three different stories.
  const importableRows = useMemo(
    () => gridFullData.filter((item) => !item.isUnknownArticle),
    [gridFullData],
  );

  const toggleSelectAll = () => {
    setSelectedIds(selectAll ? new Set<number>() : new Set(importableRows.map((v) => v.id)));
    setSelectAll(!selectAll);
  };

  const toggleCheckbox = (id: number) => {
    if (!importableRows.some((item) => item.id === id)) {
      return;
    }

    const newSelectedIds = new Set(selectedIds);
    if (newSelectedIds.has(id)) {
      newSelectedIds.delete(id);
      setSelectAll(false);
    } else {
      newSelectedIds.add(id);
      setSelectAll(importableRows.every((item) => newSelectedIds.has(item.id)));
    }
    setSelectedIds(newSelectedIds);
  };

  const selectedRows = useMemo(
    () => gridFullData.filter((item) => selectedIds.has(item.id)),
    [gridFullData, selectedIds],
  );

  const isDuplicates = useMemo(
    () => selectedRows.some((value) => value.isDuplicate),
    [selectedRows],
  );
  const isOverlaps = useMemo(
    () => selectedRows.some((value) => value.isOverlapping),
    [selectedRows],
  );
  const unknownArticlesCount = useMemo(
    () => gridFullData.filter((value) => value.isUnknownArticle).length,
    [gridFullData],
  );
  const unmappedColumns = useMemo(
    () =>
      Array.from(new Set(gridFullData.flatMap((value) => value.unmappedPriceHeaders ?? []))).sort(),
    [gridFullData],
  );

  // Only the price categories the file actually carried get a column; showing
  // ten mostly-empty ones would bury the two that matter.
  const filledPriceCategories = useMemo(
    () =>
      Array.from({ length: PRICE_CATEGORIES_COUNT }, (_, i) => i + 1).filter((category) =>
        gridFullData.some((row) => (row as Record<string, any>)[`price${category}`] != null),
      ),
    [gridFullData],
  );

  const dataToWrite = useMemo(
    () =>
      selectedRows
        .filter((item) => !item.isUnknownArticle)
        .map(
          ({
            id: _id,
            isDuplicate: _isDuplicate,
            isUnknownArticle: _isUnknownArticle,
            isOverlapping: _isOverlapping,
            unmappedPriceHeaders: _unmappedPriceHeaders,
            unresolvedPrices: _unresolvedPrices,
            __typename: _typename,
            ...keepAttrs
          }) => keepAttrs,
        ),
    [selectedRows],
  );

  const needNotExpandPossibility = gridFullData.length <= collapsedRows;
  const gridShownData = useMemo(
    () => (needNotExpandPossibility ? gridFullData : gridFullData.slice(0, collapsedRows)),
    [gridFullData, needNotExpandPossibility, collapsedRows],
  );

  // Height follows the rows actually on screen, so a three-row price list is a
  // three-row grid rather than a mostly blank block.
  const height = useMemo(
    () =>
      importGridHeight(
        visibleRows({ rowCount: gridFullData.length, collapsedRows, isAllDataShown }),
      ),
    [gridFullData.length, collapsedRows, isAllDataShown],
  );

  const runImport = () => {
    setIsAllDataShown(false);
    configurationService.pub.executeArticleSpecialPricesImport({ importData: dataToWrite, mode });
    configurationTabLoadingService.pub.loading(true);
  };

  const handleImport = () => {
    // Replacing deletes every period of the affected articles, including price
    // history the file does not mention - always confirmed explicitly.
    if (mode === C_Special_Price_Import_Mode.SPIM2_REPLACE_PER_ARTICLE) {
      setIsReplaceConfirmOpen(true);
      return;
    }
    runImport();
  };

  const legendData = useMemo(
    () => [
      {
        color: theme.palette.orange.light,
        title: t('settings.duplicate_in_import_data'),
      },
      {
        color: theme.palette.red.light,
        title: t('settings.special_prices_unknown_article'),
      },
    ],
    [t],
  );

  const alerts = useMemo(() => {
    const list: IImportAlert[] = [];

    if (isDuplicates) {
      list.push({
        severity: 'warning',
        message: t('settings.special_prices_duplicate_periods'),
      });
    }
    if (isOverlaps) {
      list.push({ severity: 'info', message: t('settings.special_prices_overlapping_periods') });
    }
    if (unknownArticlesCount > 0) {
      list.push({
        severity: 'error',
        message: t('settings.special_prices_unknown_articles', { total: unknownArticlesCount }),
      });
    }
    if (unmappedColumns.length > 0) {
      list.push({
        severity: 'warning',
        message: t('settings.special_prices_unmapped_columns', {
          columns: unmappedColumns.join(', '),
        }),
      });
    }

    return list;
  }, [isDuplicates, isOverlaps, unknownArticlesCount, unmappedColumns, t]);

  const priceCategoryLabel = (category: number): string => {
    const dictEntry = options.priceCategories.find(({ id }) => String(id) === String(category));
    return dictEntry?.label ? String(dictEntry.label) : `${t('common.price')} ${category}`;
  };

  if (!gridFullData.length) {
    return null;
  }

  const affectedArticlesCount = new Set(dataToWrite.map((item) => item.articleNo)).size;

  return (
    <Stack>
      <TableHeader
        icon={icon}
        title={t('settings.special_prices_import')}
        dataLength={gridFullData.length}
        shownDataLength={gridShownData.length}
        isAllDataShown={isAllDataShown}
        alerts={alerts}
        importButtonAvailable={dataToWrite.length > 0}
        onImport={handleImport}
      >
        <RadioGroup
          row
          value={mode}
          onChange={(e) => setMode(e.target.value as C_Special_Price_Import_Mode)}
          sx={{ mt: '4px' }}
        >
          <FormControlLabel
            value={C_Special_Price_Import_Mode.SPIM1_MERGE_PERIODS}
            control={<Radio size='small' />}
            label={t('settings.special_prices_mode_merge')}
          />
          <FormControlLabel
            value={C_Special_Price_Import_Mode.SPIM2_REPLACE_PER_ARTICLE}
            control={<Radio size='small' />}
            label={t('settings.special_prices_mode_replace')}
          />
        </RadioGroup>
      </TableHeader>
      <Table
        data={isAllDataShown ? gridFullData : gridShownData}
        height={height}
        viewRows={specialPricesViewRows}
        focusTabProps={{ enabled: true }}
      >
        {/* One flat array: the price columns are built from the data, and
        React.Children flattens the nested list while `children: ReactElement |
        ReactElement[]` does not accept a mixed JSX list. */}
        {[
          <Column
            key='select'
            disableReorder
            width={40}
            maxWidth={40}
            headerAlign='right'
            field='id'
            sortable={false}
            renderHeader={() => (
              <Box>
                <Checkbox size='small' checked={selectAll} onChange={toggleSelectAll} />
              </Box>
            )}
            renderCell={({ row }) =>
              row?.id ? (
                <Box sx={{ position: 'relative' }}>
                  <Checkbox
                    size='small'
                    disabled={row.isUnknownArticle}
                    checked={selectedIds.has(row.id)}
                    onChange={() => toggleCheckbox(row.id)}
                  />
                </Box>
              ) : null
            }
          />,
          <Column
            key='articleNo'
            field='articleNo'
            headerName={t('common.article_no')}
            width={150}
            sortable={false}
          />,
          <Column
            key='fromDate'
            field='fromDate'
            headerName={t('common.from_date')}
            width={120}
            sortable={false}
            valueGetter={(value) => (value ? localeFormatterHelper.formatDate(value) : '')}
          />,
          <Column
            key='toDate'
            field='toDate'
            headerName={t('common.to_date')}
            width={120}
            sortable={false}
            valueGetter={(value) => (value ? localeFormatterHelper.formatDate(value) : '')}
          />,
          ...filledPriceCategories.map((category) => (
            <Column
              key={`price${category}`}
              field={`price${category}`}
              headerName={priceCategoryLabel(category)}
              width={130}
              align='right'
              headerAlign='right'
              sortable={false}
              valueGetter={(value) =>
                value == null ? '' : localeFormatterHelper.formatNumber(value, { precision: 2 })
              }
            />
          )),
          <Column
            key='note'
            field='note'
            headerName={t('common.note')}
            width={200}
            sortable={false}
          />,
          <Footer
            key='footer'
            numberRows={gridFullData.length}
            legendData={legendData}
            titleRows={t('settings.special_prices_periods', {
              total: gridFullData.length,
              selected: dataToWrite.length,
              articles: affectedArticlesCount,
            })}
            height={36}
          />,
        ]}
      </Table>
      {!isAllDataShown && (
        <Button
          onClick={() => setIsAllDataShown(true)}
          variant='outlined'
          size='small'
          sx={{ width: '200px', marginTop: '16px', margin: '16px auto 0 auto' }}
        >
          {t('common.show_all')}
        </Button>
      )}
      <Dialog open={isReplaceConfirmOpen} onClose={() => setIsReplaceConfirmOpen(false)}>
        <DialogTitle>{t('settings.special_prices_mode_replace')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('settings.special_prices_replace_confirmation', {
              articles: affectedArticlesCount,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsReplaceConfirmOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant='contained'
            color='error'
            onClick={() => {
              setIsReplaceConfirmOpen(false);
              runImport();
            }}
          >
            {t('debitor.paymentsDetailsInfo.import')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

// The identity of a period, and therefore what "the same period" means to the
// merge mode on the backend: article + both dates.
const isSamePeriod = (a: ISpecialPriceImportData, b: ISpecialPriceImportData): boolean =>
  a.articleNo === b.articleNo && a.fromDate === b.fromDate && a.toDate === b.toDate;

// An open end (no toDate) runs forever, hence the far-future fallback.
const isOverlapping = (a: ISpecialPriceImportData, b: ISpecialPriceImportData): boolean => {
  if (a.articleNo !== b.articleNo) {
    return false;
  }
  const aFrom = String(a.fromDate ?? '');
  const bFrom = String(b.fromDate ?? '');
  const aTo = String(a.toDate ?? '9999-12-31');
  const bTo = String(b.toDate ?? '9999-12-31');

  return aFrom <= bTo && bFrom <= aTo;
};

interface ISpecialPricesTable {
  dataFromFile: ISpecialPriceImportData[];
  /** Rows to show before the grid is expanded - see helpers/importGridLayout. */
  collapsedRows: number;
  existingNumbers: string[];
  options: IOptionsData;
}
